import React, { useRef } from "react";
import { SlArrowLeft } from "react-icons/sl";
import { Link } from "react-router-dom";
import html2canvas from "html2canvas";

const Passes = () => {
  const otp = JSON.parse(localStorage.getItem("otp"));
  const userData = JSON.parse(localStorage.getItem("user"));
  const contentRef = useRef(null);

  const handleDownload = () => {
    html2canvas(contentRef.current).then((canvas) => {
      const dataUrl = canvas.toDataURL();
      const link = document.createElement("a");
      link.download = "gate-entry-pass.png";
      link.href = dataUrl;
      link.click();
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const time = new Date(timeString);
    return time.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getPassTypeIcon = () => {
    switch (otp?.data?.type) {
      case "guest":
        return (
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
          </svg>
        );
      case "delivery":
        return (
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
          </svg>
        );
      case "vehicle":
        return (
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v8a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm3.5 3.5a.5.5 0 000 1h7a.5.5 0 000-1h-7z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="bg-white rounded-t-2xl px-6 py-4 flex items-center shadow-lg">
          <Link
            to="/userdash"
            className="text-slate-700 hover:text-slate-900 transition-colors"
          >
            <SlArrowLeft className="w-5 h-5" />
          </Link>
          <h3 className="flex-1 text-center font-bold text-xl text-slate-800">
            Gate Entry Pass
          </h3>
          <div className="w-5"></div>
        </div>

        {/* Pass Content */}
        <div ref={contentRef} className="bg-white shadow-2xl">
          {/* Top Section with Gradient */}
          <div className="bg-gradient-to-br from-[#403F93] to-[#5B5AA8] px-6 py-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full -mr-20 -mt-20"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white opacity-5 rounded-full -ml-16 -mb-16"></div>

            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4 backdrop-blur-sm">
                <div className="text-white">{getPassTypeIcon()}</div>
              </div>
              <p className="text-white/80 text-xs uppercase tracking-widest mb-2 font-medium">
                You are invited by
              </p>
              <h2 className="text-white text-2xl font-bold uppercase tracking-wide mb-1">
                {otp?.data?.invited_by || userData?.name || "Host"}
              </h2>
              <p className="text-white/70 text-sm">to the Residency</p>
            </div>
          </div>

          {/* OTP Code Section */}
          <div className="px-6 -mt-8 relative z-20">
            <div className="bg-white rounded-2xl shadow-xl border-4 border-[#403F93] p-6">
              <p className="text-center text-xs text-slate-500 uppercase tracking-widest mb-3 font-semibold">
                Entry Code
              </p>
              <div className="flex justify-center items-center gap-2 mb-2">
                {(otp?.data?.otp || "000000").split("").map((digit, index) => (
                  <div
                    key={index}
                    className="w-10 h-12 bg-gradient-to-br from-[#403F93] to-[#5B5AA8] rounded-lg flex items-center justify-center shadow-md"
                  >
                    <span className="text-white text-2xl font-bold">
                      {digit}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-center text-xs text-slate-400 mt-3">
                Show this code at the gate
              </p>
            </div>
          </div>

          {/* Details Section */}
          <div className="px-6 py-6 space-y-4">
            {/* Pass Type Info */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-[#403F93] rounded-lg flex items-center justify-center text-white">
                  {otp?.data?.type === "guest" && (
                    <span className="text-white">👤</span>
                  )}
                  {otp?.data?.type === "delivery" && "📦"}
                  {otp?.data?.type === "vehicle" && "🚗"}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">
                    {otp?.data?.type || "Guest"} Pass
                  </p>
                  <p className="text-sm font-semibold text-slate-800">
                    {otp?.data?.type === "guest" && otp?.data?.guest_name}
                    {otp?.data?.type === "delivery" &&
                      `${otp?.data?.delivery_service} - ${otp?.data?.delivery_place}`}
                    {otp?.data?.type === "vehicle" &&
                      `${otp?.data?.vehicle_type} (${otp?.data?.vehicle_number})`}
                  </p>
                </div>
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-2">
                  Date
                </p>
                <p className="text-sm font-bold text-slate-800">
                  {formatDate(otp?.data?.date)}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-2">
                  Time
                </p>
                <p className="text-sm font-bold text-slate-800">
                  {formatTime(otp?.data?.visit_time)}
                </p>
              </div>
            </div>

            {/* Purpose */}
            {otp?.data?.purpose && (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-2">
                  Purpose
                </p>
                <p className="text-sm text-slate-700">{otp?.data?.purpose}</p>
              </div>
            )}

            {/* Phone Number */}
            {otp?.data?.phone_number && (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-2">
                  Contact
                </p>
                <p className="text-sm font-semibold text-slate-800">
                  {otp?.data?.phone_number}
                </p>
              </div>
            )}

            {/* Status Badge */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                Active Pass
              </span>
            </div>
          </div>

          {/* Footer Design */}
          <div className="bg-slate-50 px-6 py-4 border-t-2 border-dashed border-slate-300">
            <p className="text-center text-xs text-slate-500">
              Valid for single entry • Non-transferable
            </p>
          </div>
        </div>

        {/* Share Button */}
        <button
          onClick={handleDownload}
          className="w-full bg-gradient-to-r from-[#403F93] to-[#5B5AA8] text-white py-4 rounded-b-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
          </svg>
          Download & Share Pass
        </button>
      </div>
    </div>
  );
};

export default Passes;
